import {
  Application,
  Assets,
  Container,
  Sprite,
  Texture,
  Graphics,
  Rectangle,
  Text,
} from "pixi.js";
import { PuzzlePiece } from "./PuzzlePiece";
import { Confetti } from "./Confetti";
import { SoundManager } from "./SoundManager";
import type { Level } from "./types";

export class GameManager {
  private app: Application;
  private gridContainer: Container;
  private pieces: PuzzlePiece[] = [];
  private grid: (PuzzlePiece | null)[][] = [];
  private gridSize: number = 3; // 3x3 grid
  private pieceWidth: number = 0;
  private pieceHeight: number = 0;
  private imageAspectRatio: number = 1; // Width / Height of original image
  private textureCrop: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null = null; // Crop region for mobile
  private activePiece: PuzzlePiece | null = null;
  private dragOffset = { x: 0, y: 0 };
  private startGridPos = { x: 0, y: 0 };
  private currentLevelIndex: number = 0;
  private levels: Level[] = [];
  private victoryCallback?: () => void;

  private activeGroup: PuzzlePiece[] = [];
  private groupStartPositions: Map<PuzzlePiece, { x: number; y: number }> =
    new Map();
  private eventsInitialized: boolean = false;
  private activePointerId: number | null = null; // Track active pointer to prevent multi-touch issues

  // Pixi UI elements
  private uiContainer: Container;
  private confetti: Confetti;
  private soundManager: SoundManager;
  private victoryBackdrop: Graphics;

  constructor(app: Application) {
    this.app = app;
    this.app.stage.sortableChildren = true;

    this.gridContainer = new Container();
    this.gridContainer.sortableChildren = true;
    this.gridContainer.zIndex = 0;
    this.app.stage.addChild(this.gridContainer);

    // Create UI container (on top of grid)
    this.uiContainer = new Container();
    this.uiContainer.zIndex = 1000;
    this.app.stage.addChild(this.uiContainer);

    // Create victory backdrop (between grid and confetti)
    this.victoryBackdrop = new Graphics();
    this.victoryBackdrop.zIndex = 1500;
    this.victoryBackdrop.visible = false;
    this.app.stage.addChild(this.victoryBackdrop);

    // Create confetti (on top of everything)
    this.confetti = new Confetti();
    this.confetti.getContainer().zIndex = 2000;
    this.app.stage.addChild(this.confetti.getContainer());

    // Initialize sound manager
    this.soundManager = SoundManager.getInstance();

    this.createUI();
  }

  private createUI(): void {
    // No UI elements currently
  }

  public updateLevelDisplay(): void {
    // Level indicator removed
  }

  public setCollectionName(_name: string): void {
    // Level indicator removed
  }

  public async loadLevels(levels: Level[]): Promise<void> {
    console.log("Loading levels:", levels);
    this.levels = levels;

    // Initialize grid based on first level's grid size
    if (levels.length > 0) {
      this.gridSize = levels[0].gridSize;
      console.log("Initial grid size set to:", this.gridSize);
    } else {
      console.error("No levels provided!");
    }

    this.grid = Array(this.gridSize)
      .fill(null)
      .map(() => Array(this.gridSize).fill(null));

    // Calculate piece dimensions based on viewport
    this.calculatePieceDimensions();
  }

  private calculatePieceDimensions(): void {
    const EDGE_SWIPE_SAFE_ZONE = 30; // Prevents triggering browser back/forward gestures
    const verticalMargin = 4;

    const availableWidth = this.app.screen.width - EDGE_SWIPE_SAFE_ZONE * 2;
    const availableHeight = this.app.screen.height - verticalMargin * 2;

    // Calculate grid dimensions preserving aspect ratio
    // Total grid aspect ratio = imageAspectRatio (since it's gridSize x gridSize pieces)
    const gridAspectRatio = this.imageAspectRatio;

    let totalGridWidth: number;
    let totalGridHeight: number;

    if (availableWidth / availableHeight > gridAspectRatio) {
      // Screen is wider than image - fit to height
      totalGridHeight = availableHeight;
      totalGridWidth = totalGridHeight * gridAspectRatio;
    } else {
      // Screen is taller than image - fit to width
      totalGridWidth = availableWidth;
      totalGridHeight = totalGridWidth / gridAspectRatio;
    }

    // Calculate piece dimensions (preserving aspect ratio per piece)
    this.pieceWidth = Math.floor(totalGridWidth / this.gridSize);
    this.pieceHeight = Math.floor(totalGridHeight / this.gridSize);

    // Center the grid in the viewport
    const gridWidth = this.gridSize * this.pieceWidth;
    const gridHeight = this.gridSize * this.pieceHeight;

    this.gridContainer.x = (this.app.screen.width - gridWidth) / 2;
    this.gridContainer.y = (this.app.screen.height - gridHeight) / 2;
  }

  public async initLevel() {
    console.log(
      "InitLevel called for level index:",
      this.currentLevelIndex,
      "Total levels:",
      this.levels.length,
    );
    const currentLevel = this.levels[this.currentLevelIndex];
    if (!currentLevel) {
      console.error("No level data available at index", this.currentLevelIndex);
      throw new Error("No level data available");
    }
    console.log("Loading level:", currentLevel);

    // Clear previous level
    this.gridContainer.removeChildren();
    this.pieces = [];

    // Reset gridContainer transform (may have been modified by victory animation)
    this.gridContainer.scale.set(1);
    this.gridContainer.pivot.set(0, 0);

    // Update grid size if it changed
    if (currentLevel.gridSize !== this.gridSize) {
      this.gridSize = currentLevel.gridSize;
    }

    // Always reinitialize grid
    this.grid = Array(this.gridSize)
      .fill(null)
      .map(() => Array(this.gridSize).fill(null));

    let texture;
    try {
      console.log("Attempting to load image from:", currentLevel.imageUrl);
      texture = await this.loadTextureWithRetry(currentLevel.imageUrl, 3);
      console.log(
        "Successfully loaded texture for level",
        this.currentLevelIndex + 1,
        "Texture size:",
        texture.width,
        "x",
        texture.height,
      );
      // Calculate crop for mobile (portrait screens)
      const screenRatio = this.app.screen.width / this.app.screen.height;
      const imageRatio = texture.width / texture.height;

      if (screenRatio < 1 && imageRatio > 1) {
        // Portrait screen with landscape image - crop to fit better
        // Target a square-ish crop using focal point
        const targetRatio = Math.max(screenRatio, 0.75); // Don't go narrower than 3:4
        const newWidth = texture.height * targetRatio;

        // Use focal point from level data, default to center (0.5)
        const focalX = currentLevel.focalPoint?.x ?? 0.5;

        // Calculate crop X based on focal point
        // focalX 0 = left edge, 0.5 = center, 1 = right edge
        const maxCropX = texture.width - newWidth;
        const cropX = Math.max(
          0,
          Math.min(maxCropX, focalX * texture.width - newWidth / 2),
        );

        this.textureCrop = {
          x: cropX,
          y: 0,
          width: newWidth,
          height: texture.height,
        };
        this.imageAspectRatio = targetRatio;
        console.log(
          `Mobile crop: ${newWidth.toFixed(0)}x${texture.height} from ${texture.width}x${texture.height} (focal: ${focalX})`,
        );
      } else {
        // No crop needed
        this.textureCrop = null;
        this.imageAspectRatio = imageRatio;
      }

      // Preload next level's image
      this.preloadNextLevel();
    } catch (error) {
      console.error(
        "Failed to load image for level",
        this.currentLevelIndex + 1,
        "URL:",
        currentLevel.imageUrl,
        "Error:",
        error,
      );
      // Use a fallback solid color texture
      console.log("Creating fallback texture...");
      const graphics = new Graphics();
      graphics.rect(0, 0, 800, 600);
      graphics.fill({ color: 0x888888 });
      texture = this.app.renderer.generateTexture(graphics);
      this.imageAspectRatio = 800 / 600; // 4:3 fallback
      this.textureCrop = null;
      console.log("Fallback texture created");
    }

    // Calculate piece dimensions now that we know the aspect ratio
    this.calculatePieceDimensions();

    // Create pieces
    let idCounter = 0;
    const positions = [];

    // Generate positions
    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        positions.push({ x, y });
      }
    }

    // Shuffle starting positions
    const shuffledPositions = [...positions].sort(() => Math.random() - 0.5);

    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        // Calculate texture region (with optional crop for mobile)
        const crop = this.textureCrop || {
          x: 0,
          y: 0,
          width: texture.width,
          height: texture.height,
        };
        const pieceTexture = new Texture({
          source: texture.source,
          frame: new Rectangle(
            crop.x + x * (crop.width / this.gridSize),
            crop.y + y * (crop.height / this.gridSize),
            crop.width / this.gridSize,
            crop.height / this.gridSize,
          ),
        });

        const startPos = shuffledPositions.pop()!;

        const piece = new PuzzlePiece(
          idCounter++,
          pieceTexture,
          startPos.x,
          startPos.y,
          x, // correct X
          y, // correct Y
          this.pieceWidth,
          this.pieceHeight,
        );

        this.grid[startPos.y][startPos.x] = piece;
        this.pieces.push(piece);
        this.gridContainer.addChild(piece.container);

        // Setup interaction
        piece.container.on("pointerdown", (e) => this.onDragStart(piece, e));
      }
    }

    // Check initial connections (tiles randomly placed next to correct neighbors)
    for (const piece of this.pieces) {
      this.checkConnections(piece);
    }

    // Global pointer events (only set up once)
    if (!this.eventsInitialized) {
      this.app.stage.eventMode = "static";
      this.updateHitArea();
      this.app.renderer.on("resize", () => {
        this.updateHitArea();
        this.onResize();
      });

      this.app.stage.on("pointerup", this.onDragEnd.bind(this));
      this.app.stage.on("pointerupoutside", this.onDragEnd.bind(this));
      this.app.stage.on("pointermove", this.onDragMove.bind(this));
      this.eventsInitialized = true;
    }
  }

  private updateHitArea(): void {
    this.app.stage.hitArea = this.app.screen;
  }

  private onResize(): void {
    // Recalculate piece dimensions
    const oldWidth = this.pieceWidth;
    const oldHeight = this.pieceHeight;

    this.calculatePieceDimensions();

    // If dimensions changed, update all pieces
    if (oldWidth !== this.pieceWidth || oldHeight !== this.pieceHeight) {
      for (const piece of this.pieces) {
        // Update piece dimensions
        piece.updateDimensions(this.pieceWidth, this.pieceHeight);
        // Update position
        piece.updatePositionFromGrid();
      }
    }
  }

  private findConnectedGroup(piece: PuzzlePiece): PuzzlePiece[] {
    const group: PuzzlePiece[] = [];
    const visited = new Set<PuzzlePiece>();
    const queue: PuzzlePiece[] = [piece];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;

      visited.add(current);
      group.push(current);

      // Check all 4 neighbors
      const neighbors = [
        { dx: 0, dy: -1 },
        { dx: 1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: -1, dy: 0 },
      ];

      for (const { dx, dy } of neighbors) {
        const nx = current.gridX + dx;
        const ny = current.gridY + dy;

        if (this.isValidGrid(nx, ny)) {
          const neighbor = this.grid[ny][nx];
          if (neighbor && !visited.has(neighbor)) {
            // Check if they are correctly connected
            if (
              neighbor.correctGridX === current.correctGridX + dx &&
              neighbor.correctGridY === current.correctGridY + dy
            ) {
              queue.push(neighbor);
            }
          }
        }
      }
    }

    return group;
  }

  private canMoveGroup(
    group: PuzzlePiece[],
    deltaX: number,
    deltaY: number,
  ): boolean {
    // Don't allow zero movement
    if (deltaX === 0 && deltaY === 0) {
      return false;
    }

    const groupCells = new Set<string>();

    // Collect current group positions
    for (const piece of group) {
      groupCells.add(`${piece.gridX},${piece.gridY}`);
    }

    // Check if all pieces in the group can move to their new positions
    for (const piece of group) {
      const newGridX = piece.gridX + deltaX;
      const newGridY = piece.gridY + deltaY;

      // Check bounds
      if (!this.isValidGrid(newGridX, newGridY)) {
        return false;
      }

      const targetKey = `${newGridX},${newGridY}`;

      // Target is occupied by another group piece (they're moving together, ok)
      if (groupCells.has(targetKey)) {
        continue;
      }

      // Target is occupied by a piece not in the group - this is ok for swapping
      // We'll handle the swap in onDragEnd
    }

    return true;
  }

  private onDragStart(piece: PuzzlePiece, event: any) {
    // Ignore if we're already dragging with another pointer
    if (this.activePointerId !== null) return;

    // Track this pointer
    this.activePointerId = event.pointerId;

    // Find connected group
    const connectedGroup = this.findConnectedGroup(piece);

    console.log(
      `Drag Start - Piece ${piece.id} at (${piece.gridX},${piece.gridY}), Group size: ${connectedGroup.length}`,
    );

    // Only pick up the group if we can actually move it somewhere
    // Check all 4 directions
    let canMoveGroup = false;
    if (connectedGroup.length > 1) {
      const directions = [
        { dx: 1, dy: 0 },
        { dx: -1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: 0, dy: -1 },
      ];

      for (const { dx, dy } of directions) {
        if (this.canMoveGroup(connectedGroup, dx, dy)) {
          canMoveGroup = true;
          break;
        }
      }
    }

    // Decide what to pick up
    if (connectedGroup.length > 1 && canMoveGroup) {
      this.activeGroup = connectedGroup;
      this.activePiece = piece;
      console.log(`Moving group of ${connectedGroup.length} pieces`);
    } else {
      // Just move the single piece
      this.activeGroup = [piece];
      this.activePiece = piece;
      if (connectedGroup.length > 1) {
        console.log(`Group blocked, moving single piece only`);
      }
    }

    this.startGridPos = { x: piece.gridX, y: piece.gridY };

    // Store initial positions for all pieces in active group
    this.groupStartPositions.clear();
    for (const groupPiece of this.activeGroup) {
      this.groupStartPositions.set(groupPiece, {
        x: groupPiece.gridX,
        y: groupPiece.gridY,
      });
    }

    // Get local position within grid container
    const globalPos = event.global || event.data.global;
    const localPos = this.gridContainer.toLocal(globalPos);

    this.dragOffset = {
      x: localPos.x - piece.container.x,
      y: localPos.y - piece.container.y,
    };

    // Play pickup sound when starting drag
    this.soundManager.play("pickup");

    // Start drag ONLY for pieces we're actually moving
    // Disconnect from neighbors outside the group, but keep connections within the group
    for (const groupPiece of this.activeGroup) {
      groupPiece.startDrag();
      this.disconnectFromNonGroupNeighbors(groupPiece, this.activeGroup);
    }
  }

  private disconnectFromNonGroupNeighbors(
    piece: PuzzlePiece,
    group: PuzzlePiece[],
  ) {
    const neighbors = [
      { x: 0, y: -1, dir: "top", opp: "bottom" },
      { x: 1, y: 0, dir: "right", opp: "left" },
      { x: 0, y: 1, dir: "bottom", opp: "top" },
      { x: -1, y: 0, dir: "left", opp: "right" },
    ];

    for (const { x, y, dir, opp } of neighbors) {
      const nx = piece.gridX + x;
      const ny = piece.gridY + y;
      if (this.isValidGrid(nx, ny)) {
        const neighbor = this.grid[ny][nx];
        if (neighbor && !group.includes(neighbor)) {
          // Disconnect from neighbors outside the group
          piece.setConnected(dir as any, false);
          neighbor.setConnected(opp as any, false);
        }
      }
    }
  }

  private isValidGrid(x: number, y: number): boolean {
    return x >= 0 && x < this.gridSize && y >= 0 && y < this.gridSize;
  }

  private onDragMove(event: any) {
    // Only respond to the pointer that started the drag
    if (event.pointerId !== this.activePointerId) return;
    if (!this.activePiece || this.activeGroup.length === 0) return;

    const globalPos = event.global || event.data.global;
    const localPos = this.gridContainer.toLocal(globalPos);

    // Calculate new position for primary piece
    const newX = localPos.x - this.dragOffset.x;
    const newY = localPos.y - this.dragOffset.y;

    // Move primary piece
    this.activePiece.setPosition(newX, newY);

    // Move all other pieces in group relative to primary piece
    if (this.activeGroup.length > 1) {
      const primaryStartPos = this.groupStartPositions.get(this.activePiece)!;
      const deltaX = newX - primaryStartPos.x * this.pieceWidth;
      const deltaY = newY - primaryStartPos.y * this.pieceHeight;

      for (const groupPiece of this.activeGroup) {
        if (groupPiece !== this.activePiece) {
          const startPos = this.groupStartPositions.get(groupPiece)!;
          groupPiece.setPosition(
            startPos.x * this.pieceWidth + deltaX,
            startPos.y * this.pieceHeight + deltaY,
          );
        }
      }
    }
  }

  private findEmptySlots(): Array<{ x: number; y: number }> {
    const empty: Array<{ x: number; y: number }> = [];
    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        if (!this.grid[y][x]) {
          empty.push({ x, y });
        }
      }
    }
    return empty;
  }

  private onDragEnd(event: any) {
    // Only respond to the pointer that started the drag
    if (event.pointerId !== this.activePointerId) return;
    if (!this.activePiece || this.activeGroup.length === 0) return;

    const piece = this.activePiece;
    const centerX = piece.container.x + this.pieceWidth / 2;
    const centerY = piece.container.y + this.pieceHeight / 2;

    // Calculate grid index based on center of primary piece
    let newGridX = Math.floor(centerX / this.pieceWidth);
    let newGridY = Math.floor(centerY / this.pieceHeight);

    // Clamp to grid bounds
    newGridX = Math.max(0, Math.min(this.gridSize - 1, newGridX));
    newGridY = Math.max(0, Math.min(this.gridSize - 1, newGridY));

    const deltaX = newGridX - piece.gridX;
    const deltaY = newGridY - piece.gridY;

    // No movement - just snap back
    if (deltaX === 0 && deltaY === 0) {
      for (const groupPiece of this.activeGroup) {
        groupPiece.updatePositionFromGrid();
        groupPiece.endDrag(false);
      }
      this.activePiece = null;
      this.activeGroup = [];
      this.activePointerId = null;
      return;
    }

    // NEW APPROACH: Simpler and more robust
    console.log(
      `Moving ${this.activeGroup.length} piece(s) by (${deltaX},${deltaY})`,
    );

    // Step 1: Collect all pieces that will be displaced by the group
    const displaced: PuzzlePiece[] = [];
    const groupNewPositions: Array<{
      piece: PuzzlePiece;
      newX: number;
      newY: number;
    }> = [];

    for (const groupPiece of this.activeGroup) {
      const targetX = groupPiece.gridX + deltaX;
      const targetY = groupPiece.gridY + deltaY;

      // Check bounds
      if (
        targetX < 0 ||
        targetX >= this.gridSize ||
        targetY < 0 ||
        targetY >= this.gridSize
      ) {
        // Group can't move - out of bounds
        console.log(`Group move blocked - out of bounds`);
        for (const p of this.activeGroup) {
          p.updatePositionFromGrid();
          p.endDrag(false);
        }
        this.activePiece = null;
        this.activeGroup = [];
        this.activePointerId = null;
        return;
      }

      groupNewPositions.push({
        piece: groupPiece,
        newX: targetX,
        newY: targetY,
      });

      const targetPiece = this.grid[targetY][targetX];
      if (
        targetPiece &&
        !this.activeGroup.includes(targetPiece) &&
        !displaced.includes(targetPiece)
      ) {
        displaced.push(targetPiece);
      }
    }

    // Step 2: Clear all involved positions
    for (const groupPiece of this.activeGroup) {
      this.grid[groupPiece.gridY][groupPiece.gridX] = null;
    }
    for (const dispPiece of displaced) {
      this.grid[dispPiece.gridY][dispPiece.gridX] = null;
    }

    // Step 3: Place the group in new positions
    for (const { piece: groupPiece, newX, newY } of groupNewPositions) {
      groupPiece.gridX = newX;
      groupPiece.gridY = newY;
      this.grid[newY][newX] = groupPiece;
    }

    // Step 4: Place displaced pieces into empty slots, preserving their relative positions
    const emptySlots = this.findEmptySlots();

    if (displaced.length > 0 && emptySlots.length >= displaced.length) {
      // Sort displaced pieces by their original grid position (top-left first)
      displaced.sort((a, b) => {
        if (a.gridY !== b.gridY) return a.gridY - b.gridY;
        return a.gridX - b.gridX;
      });

      // Sort empty slots the same way
      emptySlots.sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
      });

      // Place displaced pieces in sorted empty slots to preserve relative order
      for (let i = 0; i < displaced.length; i++) {
        const dispPiece = displaced[i];
        const slot = emptySlots[i];
        dispPiece.gridX = slot.x;
        dispPiece.gridY = slot.y;
        this.grid[slot.y][slot.x] = dispPiece;
      }
    }

    // Animate pieces to their grid positions
    for (const groupPiece of this.activeGroup) {
      const targetX = groupPiece.gridX * this.pieceWidth;
      const targetY = groupPiece.gridY * this.pieceHeight;
      groupPiece.animateToPosition(targetX, targetY, () => {
        // Play snap animation when piece settles
        groupPiece.playSnapAnimation();
      });
    }

    // Animate displaced pieces to their new positions
    for (const dispPiece of displaced) {
      const targetX = dispPiece.gridX * this.pieceWidth;
      const targetY = dispPiece.gridY * this.pieceHeight;
      dispPiece.animateToPosition(targetX, targetY);
    }

    // Debug: verify grid integrity
    this.verifyGridIntegrity();

    // Count connections before recalculating (within-group connections are preserved)
    let connectionsBefore = 0;
    for (const piece of this.activeGroup) {
      connectionsBefore += piece.getConnectionCount();
    }

    // Check connections for moved pieces
    for (const piece of this.activeGroup) {
      this.checkConnections(piece);
    }

    // Count connections after
    let connectionsAfter = 0;
    for (const piece of this.activeGroup) {
      connectionsAfter += piece.getConnectionCount();
    }

    // Also update connections for displaced pieces
    for (const piece of displaced) {
      this.checkConnections(piece);
    }

    // Play connect sound only if moved pieces made new connections
    if (connectionsAfter > connectionsBefore) {
      this.soundManager.play("connect");
      navigator.vibrate?.(20);
    }

    // Check for victory after connections update
    this.checkVictory();

    // End drag for all pieces in group
    for (const groupPiece of this.activeGroup) {
      groupPiece.endDrag(true);
    }

    this.activePiece = null;
    this.activeGroup = [];
    this.activePointerId = null;
  }

  private verifyGridIntegrity(): void {
    const seenPieces = new Set<PuzzlePiece>();
    let errors = false;

    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        const piece = this.grid[y][x];
        if (!piece) {
          console.error(`Empty slot at (${x},${y})`);
          errors = true;
        } else {
          if (seenPieces.has(piece)) {
            console.error(
              `Duplicate piece ${piece.id} found at (${x},${y}) - also at (${piece.gridX},${piece.gridY})`,
            );
            errors = true;
          }
          seenPieces.add(piece);

          if (piece.gridX !== x || piece.gridY !== y) {
            console.error(
              `Piece ${piece.id} position mismatch: grid says (${x},${y}) but piece says (${piece.gridX},${piece.gridY})`,
            );
            errors = true;
          }
        }
      }
    }

    if (seenPieces.size !== this.pieces.length) {
      console.error(
        `Grid has ${seenPieces.size} pieces but pieces array has ${this.pieces.length}`,
      );
      errors = true;
    }

    if (errors) {
      console.error(
        "Grid state:",
        this.grid.map((row) => row.map((p) => p?.id ?? "null")),
      );
    }
  }

  private checkConnections(piece: PuzzlePiece): boolean {
    // Check all 4 sides
    const neighbors = [
      { x: 0, y: -1, dir: "top", opp: "bottom" },
      { x: 1, y: 0, dir: "right", opp: "left" },
      { x: 0, y: 1, dir: "bottom", opp: "top" },
      { x: -1, y: 0, dir: "left", opp: "right" },
    ];

    let anyConnection = false;

    for (const { x, y, dir, opp } of neighbors) {
      const nx = piece.gridX + x;
      const ny = piece.gridY + y;

      if (this.isValidGrid(nx, ny)) {
        const neighbor = this.grid[ny][nx];
        if (neighbor) {
          // Check if they are logically connected in the original image
          if (
            neighbor.correctGridX === piece.correctGridX + x &&
            neighbor.correctGridY === piece.correctGridY + y
          ) {
            // Match found!
            piece.setConnected(dir as any, true);
            neighbor.setConnected(opp as any, true);
            anyConnection = true;
          } else {
            // Not a match
            piece.setConnected(dir as any, false);
            neighbor.setConnected(opp as any, false);
          }
        } else {
          piece.setConnected(dir as any, false);
        }
      } else {
        piece.setConnected(dir as any, false);
      }
    }

    if (anyConnection) {
      piece.flashCorrect();
    }

    return anyConnection;
  }

  private checkVictory(): void {
    // Check if all pieces are in correct position
    for (const piece of this.pieces) {
      if (
        piece.gridX !== piece.correctGridX ||
        piece.gridY !== piece.correctGridY
      ) {
        return; // Not solved yet
      }
    }

    // Victory!
    console.log("Level completed!");

    // Run victory sequence
    this.playVictorySequence();
  }

  private async playVictorySequence(): Promise<void> {
    // Play victory sound after a short delay (don't block animation)
    setTimeout(() => this.soundManager.play("victory"), 500);

    // Step 1: Hide piece effects (borders/bevels fade out) to reveal clean photo
    await Promise.all(this.pieces.map((piece) => piece.hideEffects()));

    // Step 2: Scale up the completed image slightly
    await this.animatePhotoReveal();

    // Step 3: Trigger confetti
    this.triggerConfetti();

    // Step 4: Wait to showcase photo with confetti
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Step 5: Show backdrop and victory popup
    this.showVictoryBackdrop();

    if (this.victoryCallback) {
      this.victoryCallback();
    }
  }

  private animatePhotoReveal(): Promise<void> {
    return new Promise((resolve) => {
      const duration = 400;
      const startTime = Date.now();
      const startScale = 1;
      const endScale = 1.05;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const scale = startScale + (endScale - startScale) * eased;

        // Scale from center of grid
        const centerX = (this.gridSize * this.pieceWidth) / 2;
        const centerY = (this.gridSize * this.pieceHeight) / 2;

        this.gridContainer.scale.set(scale);
        this.gridContainer.pivot.set(centerX, centerY);
        this.gridContainer.x = this.app.screen.width / 2;
        this.gridContainer.y = this.app.screen.height / 2;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }

  private showVictoryBackdrop(): void {
    console.log(
      "showVictoryBackdrop called",
      this.app.screen.width,
      this.app.screen.height,
    );
    this.victoryBackdrop.clear();
    this.victoryBackdrop.rect(
      0,
      0,
      this.app.screen.width,
      this.app.screen.height,
    );
    this.victoryBackdrop.fill({ color: 0x000000, alpha: 0.7 });
    this.victoryBackdrop.visible = true;
    console.log(
      "backdrop visible:",
      this.victoryBackdrop.visible,
      "zIndex:",
      this.victoryBackdrop.zIndex,
    );
  }

  public hideVictoryBackdrop(): void {
    this.victoryBackdrop.visible = false;
  }

  private getResponsiveImageUrls(baseUrl: string): {
    webp: string;
    jpeg: string;
  } {
    // If it's a local image path without extension, add size suffix
    if (baseUrl.startsWith("/images/pexels-") && !baseUrl.includes(".")) {
      const screenSize = Math.max(
        this.app.screen.width,
        this.app.screen.height,
      );
      const pixelRatio = window.devicePixelRatio || 1;
      const targetSize = screenSize * pixelRatio;

      // Available sizes: 480, 800, 1200, 1920
      // Pick the smallest size that's >= targetSize, or largest available
      let size = 1920;
      if (targetSize <= 480) size = 480;
      else if (targetSize <= 800) size = 800;
      else if (targetSize <= 1200) size = 1200;

      return {
        webp: `${baseUrl}-${size}.webp`,
        jpeg: `${baseUrl}-${size}.jpeg`,
      };
    }
    return { webp: baseUrl, jpeg: baseUrl };
  }

  private async loadTextureWithRetry(
    url: string,
    retries: number,
  ): Promise<Texture> {
    const urls = this.getResponsiveImageUrls(url);

    // Try WebP first, fall back to JPEG
    try {
      return await Assets.load(urls.webp);
    } catch {
      // WebP failed, try JPEG
    }

    // JPEG with retries
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await Assets.load(urls.jpeg);
      } catch (error) {
        if (attempt < retries) {
          console.log(`Retry ${attempt + 1}/${retries} for ${urls.jpeg}`);
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * (attempt + 1)),
          );
        } else {
          throw error;
        }
      }
    }
    throw new Error(`Failed to load ${urls.jpeg} after ${retries} retries`);
  }

  private preloadNextLevel(): void {
    const nextIndex = this.currentLevelIndex + 1;
    if (nextIndex < this.levels.length) {
      const nextLevel = this.levels[nextIndex];
      const urls = this.getResponsiveImageUrls(nextLevel.imageUrl);
      // Try to preload WebP, fall back to JPEG
      Assets.load(urls.webp).catch(() => {
        Assets.load(urls.jpeg).catch(() => {
          // Silently fail - it's just preloading
        });
      });
    }
  }

  private triggerConfetti(): void {
    const width = this.app.screen.width;
    const height = this.app.screen.height;

    // Burst from bottom corners
    this.confetti.burstDirectional(width * 0.1, height + 100, 80, -0.4, -10);
    this.confetti.burstDirectional(width * 0.9, height + 100, 80, 0.4, -10);
  }

  public setVictoryCallback(callback: () => void): void {
    this.victoryCallback = callback;
  }

  public async nextLevel(): Promise<boolean> {
    this.hideVictoryBackdrop();

    this.currentLevelIndex++;
    if (this.currentLevelIndex >= this.levels.length) {
      return false; // No more levels
    }

    // Clear current level
    this.clearLevel();

    // Load next level
    await this.initLevel();
    return true;
  }

  private clearLevel(): void {
    // Remove all pieces
    for (const piece of this.pieces) {
      this.gridContainer.removeChild(piece.container);
    }
    this.pieces = [];

    // Clear grid
    this.grid = Array(this.gridSize)
      .fill(null)
      .map(() => Array(this.gridSize).fill(null));

    // Reset gridContainer transform (was modified by victory animation)
    this.gridContainer.scale.set(1);
    this.gridContainer.pivot.set(0, 0);
  }

  public getCurrentLevel(): number {
    return this.currentLevelIndex + 1; // 1-indexed for display
  }

  public getTotalLevels(): number {
    return this.levels.length;
  }

  public setCurrentLevel(level: number): void {
    // Convert from 1-indexed to 0-indexed
    this.currentLevelIndex = Math.max(
      0,
      Math.min(level - 1, this.levels.length - 1),
    );
  }

  public hide(): void {
    this.gridContainer.visible = false;
    this.uiContainer.visible = false;
    this.hideVictoryBackdrop();
  }

  public show(): void {
    this.gridContainer.visible = true;
    this.uiContainer.visible = true;
  }
}
